import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { createApiClient, type ApiClient } from './api-client'
import { useAuth } from './auth-context'
import { getRuntimeConfig } from './config'

export interface ModuleApiContextValue {
  /** id des Moduls, zu dem die aktuelle Route gehört. */
  id: string
  /** Basis-Pfad der Modul-API, z.B. `/api/wartung`. */
  apiBasePath: string
  /** Alle von der Shell gefundenen Module mit ihren sichtbaren Namen. */
  availableModules?: readonly ModuleReference[]
}

export interface ModuleReference {
  id: string
  title: string
}

const ModuleApiContext = createContext<ModuleApiContextValue | null>(null)

/**
 * Setzt den API-Kontext für die Routen eines Moduls. Das macht die Shell —
 * ein Modul ruft das nie selbst auf.
 *
 * @example
 * ```tsx
 * <ModuleApiProvider module={{ id, apiBasePath }}>
 *   <Outlet />
 * </ModuleApiProvider>
 * ```
 */
export function ModuleApiProvider({
  module,
  children,
}: {
  module: ModuleApiContextValue
  children: ReactNode
}) {
  const value = useMemo(
    () => ({
      id: module.id,
      apiBasePath: module.apiBasePath,
      availableModules: module.availableModules ?? [],
    }),
    [module.id, module.apiBasePath, module.availableModules],
  )
  return <ModuleApiContext value={value}>{children}</ModuleApiContext>
}

/** Das Modul, zu dem die aktuelle Route gehört. */
export function useModuleApiContext(): ModuleApiContextValue {
  const context = useContext(ModuleApiContext)
  if (!context) {
    throw new Error(
      [
        'useApiClient() wurde außerhalb einer Modul-Route verwendet.',
        'Der API-Client kennt seine Basis-Adresse aus dem Manifest des Moduls; dafür muss die',
        'Komponente unterhalb der Routen aus module.config.ts gerendert werden.',
        'Wenn Sie von der Startseite aus etwas laden wollen, tun Sie das im homeWidget des Moduls.',
      ].join('\n'),
    )
  }
  return context
}

/** Alle aktiven Module, so wie die Shell sie in der Navigation kennt. */
export function useAvailableModules(): readonly ModuleReference[] {
  return useModuleApiContext().availableModules ?? []
}

/**
 * Der API-Client des aufrufenden Moduls. Kennt Basis-Adresse, Scopes und
 * Fehlerformat — ein Modul muss sich um Anmeldung und URLs nicht kümmern.
 *
 * @example
 * ```tsx
 * const api = useApiClient()
 *
 * const query = useQuery({
 *   queryKey: ['vorgaenge', page],
 *   queryFn: ({ signal }) => api.get<VorgangListe>('/vorgaenge', { query: { page }, signal }),
 * })
 *
 * const speichern = useMutation({
 *   mutationFn: (werte: VorgangEingabe) => api.post<Vorgang>('/vorgaenge', werte),
 * })
 * ```
 */
export function useApiClient(): ApiClient {
  const module = useModuleApiContext()
  const { getAccessToken, reportApiAuthError } = useAuth()

  /*
   * Der Scope kommt aus der Laufzeitkonfiguration, nicht aus dem Manifest.
   *
   * Es gibt eine App-Registrierung für die gesamte Plattform-API mit genau
   * einem Bereich; jede Modul-API liegt hinter demselben Host und wird mit
   * demselben Token angesprochen. Der Bereichsname enthält die Umgebung
   * (.../portal-test/... gegenüber .../portal/...) — im Manifest stünde er
   * fest im Bundle und wäre in einer der beiden Umgebungen falsch. Dasselbe
   * Abbild läuft aber in beiden.
   *
   * Im Mock-Betrieb ist der Wert leer; getAccessToken gibt dann null zurück
   * und der Aufruf geht ohne Authorization-Kopfzeile hinaus. Genau so soll es
   * beim Entwickeln ohne Entra sein.
   */
  const { platformScope } = getRuntimeConfig()
  const scopes = useMemo(() => (platformScope ? [platformScope] : []), [platformScope])

  return useMemo(
    () =>
      createApiClient({
        baseUrl: module.apiBasePath,
        scopes,
        getAccessToken,
        onAuthError: reportApiAuthError,
      }),
    [module.apiBasePath, scopes, getAccessToken, reportApiAuthError],
  )
}
